<script>
import axios from '@/utils/axios';
import { Offcanvas } from 'bootstrap';

const SESSIONS = [
  { value: 'full_day', label: 'Full Day' },
  { value: 'half_day_morning', label: 'Half Day (Morning)' },
  { value: 'half_day_afternoon', label: 'Half Day (Afternoon)' },
  { value: 'short_day_morning', label: 'Short Day (Morning)' },
  { value: 'short_day_afternoon', label: 'Short Day (Afternoon)' },
];

export default {
  emits: ['updated'],
  data() {
    return {
      saving: false,
      leaveTypes: [],
      sessions: SESSIONS,
      form: { leave_type_id: '', from_date: '', to_date: '', session: 'full_day', reason: '' },
      errors: {},
      oc: null,
    };
  },
  computed: {
    todayStr() {
      const t = new Date();
      return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    },
  },
  mounted() {
    this.fetchLeaveTypes();
  },
  methods: {
    async fetchLeaveTypes() {
      try {
        const res = await axios.get('/hrms/leave-types/dropdown');
        this.leaveTypes = res.data.data || [];
      } catch (e) { console.log(e.message); }
    },
    open(prefill = {}) {
      this.reset();
      if (prefill.leave_type_id) this.form.leave_type_id = prefill.leave_type_id;
      this.oc = Offcanvas.getOrCreateInstance(this.$refs.oc);
      this.oc.show();
    },
    close() { this.oc?.hide(); },
    reset() {
      this.form = { leave_type_id: '', from_date: '', to_date: '', session: 'full_day', reason: '' };
      this.errors = {};
    },
    errText(field) {
      const v = this.errors[field];
      if (!v) return '';
      return Array.isArray(v) ? v[0] : v;
    },
    validate() {
      const e = {};
      if (!this.form.leave_type_id) e.leave_type_id = 'Leave type is required';
      if (!this.form.from_date) e.from_date = 'From date is required';
      if (!this.form.to_date) e.to_date = 'To date is required';
      if (this.form.from_date && this.form.to_date && this.form.to_date < this.form.from_date)
        e.to_date = 'To date must be after from date';
      if (!this.form.session) e.session = 'Session is required';
      this.errors = e;
      return !Object.keys(e).length;
    },
    async submit() {
      if (!this.validate()) return;
      this.saving = true;
      try {
        const res = await axios.post('/hrms/leaves/request', {
          leave_type_id: this.form.leave_type_id,
          from_date: this.form.from_date,
          to_date: this.form.to_date,
          session: this.form.session,
          reason: this.form.reason || null,
        });
        this.$swalToast(res.data.message || 'Leave request submitted.');
        this.$emit('updated');
        this.close();
        this.reset();
      } catch (error) {
        if (error.type === 'validation') this.errors = error.errors;
        else this.$swalToast(error.message, 'error');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div ref="oc" class="offcanvas offcanvas-end lv-oc" tabindex="-1" id="hrm_leave_apply_modal">
    <div class="offcanvas-header">
      <div class="d-flex align-items-center gap-2">
        <span class="lv-oc-ico"><i class="ti ti-calendar-plus"></i></span>
        <h5 class="offcanvas-title mb-0">Apply for Leave</h5>
      </div>
      <button type="button" class="btn-close" data-bs-dismiss="offcanvas"></button>
    </div>

    <div class="offcanvas-body">
      <div class="mb-3">
        <label class="lv-lbl">Leave Type <span class="text-danger">*</span></label>
        <select class="form-select" v-model="form.leave_type_id" :class="{ 'is-invalid': errText('leave_type_id') }">
          <option value="">Select leave type</option>
          <option v-for="t in leaveTypes" :key="t.id" :value="t.id">{{ t.code }}</option>
        </select>
        <div v-if="errText('leave_type_id')" class="lv-err">{{ errText('leave_type_id') }}</div>
      </div>

      <div class="row g-2 mb-3">
        <div class="col-6">
          <label class="lv-lbl">From <span class="text-danger">*</span></label>
          <input type="date" class="form-control" :min="todayStr" v-model="form.from_date" :class="{ 'is-invalid': errText('from_date') }" />
          <div v-if="errText('from_date')" class="lv-err">{{ errText('from_date') }}</div>
        </div>
        <div class="col-6">
          <label class="lv-lbl">To <span class="text-danger">*</span></label>
          <input type="date" class="form-control" :min="form.from_date || todayStr" v-model="form.to_date" :class="{ 'is-invalid': errText('to_date') }" />
          <div v-if="errText('to_date')" class="lv-err">{{ errText('to_date') }}</div>
        </div>
      </div>

      <div class="mb-3">
        <label class="lv-lbl">Session <span class="text-danger">*</span></label>
        <select class="form-select" v-model="form.session" :class="{ 'is-invalid': errText('session') }">
          <option v-for="s in sessions" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
        <div v-if="errText('session')" class="lv-err">{{ errText('session') }}</div>
      </div>

      <div class="mb-3">
        <label class="lv-lbl">Reason</label>
        <textarea class="form-control" rows="4" maxlength="500" v-model="form.reason" placeholder="Optional — reason for leave"></textarea>
        <div class="lv-count">{{ form.reason.length }}/500</div>
      </div>
    </div>

    <div class="lv-oc-footer">
      <button type="button" class="btn btn-light" data-bs-dismiss="offcanvas">Cancel</button>
      <button type="button" class="btn btn-primary" :disabled="saving" @click="submit">
        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
        {{ saving ? 'Submitting…' : 'Submit Request' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.lv-oc { width: 440px; max-width: 92vw; }
.offcanvas-header { border-bottom: 1px solid #f1f5f9; padding: 18px 22px; }
.offcanvas-title { font-size: 16px; font-weight: 700; color: #0f172a; }
.lv-oc-ico { width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #4f46e5, #7c3aed); color: #fff;
  display: inline-flex; align-items: center; justify-content: center; font-size: 18px; }
.offcanvas-body { padding: 22px; }
.lv-lbl { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 5px; display: block; }
.form-control, .form-select { border-radius: 10px; border-color: #e2e8f0; font-size: 13.5px; padding: 9px 12px; }
.form-control:focus, .form-select:focus { border-color: #a5b4fc; box-shadow: 0 0 0 3px rgba(79,70,229,.1); }
.lv-err { font-size: 11px; color: #dc2626; margin-top: 4px; }
.lv-count { font-size: 10.5px; color: #94a3b8; text-align: right; margin-top: 3px; }
.lv-oc-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 22px; border-top: 1px solid #f1f5f9; margin-top: auto; }
.lv-oc-footer .btn { border-radius: 10px; font-size: 13px; font-weight: 600; padding: 9px 18px; }
.btn-primary { background: linear-gradient(135deg, #4f46e5, #7c3aed); border: none; }
.btn-light { background: #f8fafc; border: 1px solid #e2e8f0; color: #475569; }
</style>